import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { useRole } from "./useRole";

import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Divider,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Radio,
  RadioGroup,
  Slider,
  Spinner,
  Switch,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Checkbox
} from "@fluentui/react-components";
import { ArrowDownload24Regular, Dismiss24Regular } from "@fluentui/react-icons";
import { MAX_VOLUME_LABELS, calcLevel, calcGainMultiplier } from "../../utils/avatar-volume-utils";
import { useAllAvatarVolume, useLocalAudio } from "./useAvatarVolume";
import { FluentNumericInput } from "../fluent/NumericInputCompound";
import { useRoomPersistence } from "../room-persistence/RoomPersistenceContext";
import useBreakpoints from "../hooks/useBreakpoints";
import configManager from "../../singleton/ConfigManager";
import { getChatHistoryExportState, getTranscriptionHistoryExportState } from "./chat-history-export-state";
import {
  getParticipantSettingsBulkHostAction,
  getParticipantSettingsBulkHostTargets,
  getParticipantSettingsHideAction,
  getParticipantSettingsKickAction,
  getParticipantSettingsKickTargets,
  getParticipantSettingsMediaShareAction,
  getParticipantSettingsMediaShareTargets,
  getParticipantSettingsMicrophoneAction
} from "./participant-settings-actions";
import {
  PARTICIPANT_LAYOUT_FORCED_STATE,
  createParticipantLayoutRoomStateForForcedStateChange,
  normalizeParticipantLayoutForcedState
} from "../layout/participant-layout/participant-layout-room-state";
import { useParticipantLayoutDialogMediaState } from "../layout/participant-layout/useParticipantLayoutDialogMediaState";
import { MediaDevices } from "../../utils/media-devices-utils";
import { ResponsiveOverlayDrawer } from "../drawer/ResponsiveOverlayDrawer";

const MIN = 0;
const MAX = MAX_VOLUME_LABELS - 1;
const PARTICIPANT_LAYOUT_MODE_LABEL = "Mode";
const HISTORY_DOWNLOAD_HELPER_TEXT = "Only the Verse creator can download chat and transcription history.";

const useStyles = makeStyles({
  drawer: {
    "& > *": {
      scrollbarWidth: "thin",
      "&::-webkit-scrollbar-button": {
        display: "none"
      },
      "&::-webkit-scrollbar-button:vertical:decrement, &::-webkit-scrollbar-button:vertical:increment": {
        display: "none", // Specifically hide the up/down buttons
        height: "0" // Ensure no space is left for the button
      }
    }
  },
  drawerDesktop: {
    width: "400px"
  },
  headerDrawer: {
    display: " flex",
    padding: " var(--Font-size-600, 24px) 16px 12px var(--Font-size-600, 24px)",
    alignItems: " flex-start",
    gap: " 8px",
    alignSelf: " stretch",
    backgroundColor: tokens.colorNeutralBackground1
  },
  drawerHeaderTitle: {
    width: "100%"
  },
  drawerBody: {},
  search: {
    backgroundColor: "rgba(255, 255, 255, 0.10)",
    width: "100%"
  },
  section: {
    display: " flex",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "2px"
  },
  sectionHeader: {
    display: " flex",
    padding: " 8px 0px",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "var(--Font-size-100, 10px)",
    alignSelf: "stretch"
  },
  sectionHeaderText: {
    color: "var(--Gray-3, #828282)"
  },
  sectionContent: {
    width: "100%",
    display: " flex",
    padding: " 4px",
    alignItems: " center",
    height: "48px",
    borderRadius: "4px",
    justifyContent: "space-between",
    gap: " 8px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)"
    }
  },
  persona: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "32px",
    "& .fui-Avatar__image": {
      width: "32px",
      height: "32px"
    },
    "& .fui-Avatar": {
      height: "32px"
    },
    "& .fui-Persona": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "32px"
    }
  },
  icons: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "& > *": {
      cursor: "pointer"
    }
  },
  divider: {
    padding: "5px 0px 4px 0px"
  },
  contentParticipants: {},
  footerParticipants: {
    display: "flex",
    padding: "16px var(--Font-size-600, 24px) var(--Font-size-600, 24px) var(--Font-size-600, 24px)",
    gap: tokens.spacingHorizontalS,
    justifyContent: "flex-end",
    width: "100%",
    backgroundColor: tokens.colorNeutralBackground1
  },
  footerParticipantsSplit: {
    justifyContent: "space-between"
  },
  redIcon: {
    color: "var(--Red, #EB5757)"
  },
  accordionPanel: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    marginRight: "0px!important",
    "&:last-child": {
      marginBottom: "8px"
    },
    "& .fui-Field": {
      gridTemplateColumns: "1fr 1fr",
      "& .fui-Switch": {
        justifyContent: "flex-end"
      }
    }
  },
  dialogContent: {
    display: " flex",
    padding: " var(--Vertical-None, 0px) var(--Horizontal-None, 0px)",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: " var(--Vertical-SNudge, 6px)",
    alignSelf: " stretch",
    overflow: "hidden"
  },
  dialogSurface: {
    maxWidth: "480px"
  },
  volumeContainer: {
    display: "flex",
    gap: "8px"
  },
  compoundNumericInput: {
    minWidth: "60px",
    backgroundColor: "rgba(245, 245, 245, 0.10)"
  },
  fieldActionButton: {
    minWidth: "162px",
    marginLeft: "auto",
    justifySelf: "end"
  },
  chatHistoryError: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200
  },
  historySectionDescription: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    textAlign: "left",
    overflowWrap: "anywhere"
  },
  historyActionRow: {
    display: "grid",
    width: "100%",
    minWidth: 0,
    gridTemplateColumns: "minmax(0, 1fr) auto",
    alignItems: "center",
    columnGap: tokens.spacingHorizontalM
  },
  historyActionLabel: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightSemibold,
    textAlign: "left",
    overflowWrap: "anywhere"
  },
  layoutManagementSection: {
    display: "flex",
    width: "100%",
    flexDirection: "column",
    gap: tokens.spacingVerticalL
  },
  layoutBehaviorRow: {
    display: "flex",
    width: "100%",
    flexDirection: "column",
    gap: tokens.spacingVerticalS
  },
  layoutBehaviorOptions: {
    display: "flex",
    width: "100%",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS
  },
  layoutBehaviorRadio: {
    display: "grid",
    width: "100%",
    minWidth: 0,
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    columnGap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalS,
    paddingRight: 0,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: 0,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    transitionDuration: "0.15s",
    transitionProperty: "background-color, color",
    transitionTimingFunction: "ease-in-out",
    "& .fui-Radio__input": {
      left: "auto",
      right: 0
    },
    "& .fui-Radio__indicator": {
      gridColumnStart: "2",
      gridRowStart: "1",
      justifySelf: "end",
      marginRight: 0
    },
    "& .fui-Radio__label": {
      gridColumnStart: "1",
      gridRowStart: "1",
      minWidth: 0,
      paddingTop: 0,
      paddingRight: 0,
      paddingBottom: 0,
      paddingLeft: 0
    }
  },
  layoutBehaviorRadioContent: {
    display: "flex",
    minWidth: 0,
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS
  },
  layoutBehaviorRadioText: {
    width: "100%",
    minWidth: 0,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightSemibold,
    textAlign: "left",
    overflowWrap: "anywhere"
  },
  layoutBehaviorDescription: {
    width: "100%",
    minWidth: 0,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    textAlign: "left",
    overflowWrap: "anywhere"
  }
});

const PARTICIPANT_LAYOUT_BEHAVIOR_OPTIONS = [
  {
    value: PARTICIPANT_LAYOUT_FORCED_STATE.NONE,
    label: "Let users choose",
    description: "Participants can choose the view they prefer."
  },
  {
    value: PARTICIPANT_LAYOUT_FORCED_STATE.OPEN,
    label: "Force everyone to 2D",
    description: "Everyone uses the 2D view."
  },
  {
    value: PARTICIPANT_LAYOUT_FORCED_STATE.CLOSED,
    label: "Force everyone to 3D",
    description: "Everyone uses the standard 3D Verse view."
  }
];

export default function FluentSettingParticipantOverlay({
  people,
  hubChannel,
  isOpen,
  onMute,
  onMuteAll,
  onModeratePeerMedia,
  onModeratePeerMediaAll,
  person,
  allMuted,
  allHost,
  onClose,
  mySessionId,
  participantLayoutRoomEnabled,
  participantLayoutForcedState,
  onParticipantLayoutRoomStateChange
}) {
  const { id: userId, profile, roles } = person || {};

  const styles = useStyles();
  const { cameraMediaStreamsByOwnerId, screenShares } = useParticipantLayoutDialogMediaState({ mySessionId });
  const cameraShareOwnerIds = useMemo(
    () => new Set(Object.keys(cameraMediaStreamsByOwnerId || {})),
    [cameraMediaStreamsByOwnerId]
  );
  const screenShareOwnerIds = useMemo(
    () => new Set(screenShares.map(screenShare => screenShare.ownerId)),
    [screenShares]
  );
  const cameraShareTargets = useMemo(
    () => getParticipantSettingsMediaShareTargets(people, cameraShareOwnerIds),
    [cameraShareOwnerIds, people]
  );
  const screenShareTargets = useMemo(
    () => getParticipantSettingsMediaShareTargets(people, screenShareOwnerIds),
    [people, screenShareOwnerIds]
  );

  const [isOwner, setIsOwner] = useState(!!roles?.owner);
  const [isHidden, setIsHidden] = useState(() => Boolean(person && hubChannel?.isHidden?.(person.id)));
  const isCreator = !!roles?.creator;

  // Old Functionalities commented to use it when is needed
  // const hasMicPresence = !!person?.micPresence;
  // const isNetworkMuted = person?.micPresence?.muted;
  // const mayMute = !isNetworkMuted && hubChannel.canOrWillIfCreator("mute_users");
  // const mayKick = hubChannel.canOrWillIfCreator("kick_users");
  // const mayAddOwner = hubChannel.canOrWillIfCreator("update_roles") && !isOwner && !isCreator;
  // const mayRemoveOwner = hubChannel.canOrWillIfCreator("update_roles") && isOwner && !isCreator;

  useEffect(() => {
    setIsOwner(!!roles?.owner);
    setIsHidden(Boolean(person && hubChannel?.isHidden?.(person.id)));
  }, [person]);

  const onToggleHost = async () => {
    if (!isOwner) {
      await hubChannel.addOwner(userId);
      setIsOwner(true);
    } else {
      await hubChannel.removeOwner(userId);
      setIsOwner(false);
    }
  };

  const makeAllHosts = async () => {
    for (const person of getParticipantSettingsBulkHostTargets(people)) {
      await hubChannel.addOwner(person.id);
    }
  };

  const kick = useCallback(async () => {
    await hubChannel.kick(userId);
    onClose();
  }, [hubChannel, userId]);

  const kickAll = useCallback(async () => {
    for (const person of getParticipantSettingsKickTargets(people)) {
      await hubChannel.kick(person.id);
    }
    onClose();
  }, [hubChannel, people]);

  const toggleHidden = useCallback(() => {
    if (!person || !userId) {
      return;
    }

    if (isHidden) {
      hubChannel.unhide(userId);
    } else {
      hubChannel.hide(userId);
    }

    setIsHidden(!isHidden);
  }, [hubChannel, isHidden, person, userId]);

  const canUpdateRoles = !!hubChannel?.canOrWillIfCreator?.("update_roles");
  const canModerateMedia = !!hubChannel?.canOrWillIfCreator?.("mute_users");
  const participantLayoutConfigForceEnabled = !!configManager.config.participantLayout?.forceEnabled;
  const canManageParticipantLayout =
    !person &&
    !!configManager.config.participantLayout?.enabled &&
    !participantLayoutConfigForceEnabled &&
    !!onParticipantLayoutRoomStateChange &&
    !!hubChannel?.can?.("update_hub");
  const canManageChatHistoryExport = !person && !!hubChannel?.can?.("update_hub");
  const breakpoint = useBreakpoints();
  const isMobile = breakpoint === "sm" || breakpoint === "xs";

  return (
    <ResponsiveOverlayDrawer
      className={mergeClasses(styles.drawer, !isMobile && styles.drawerDesktop)}
      desktopPosition="end"
      modalType="non-modal"
      open={isOpen}
      size="full"
    >
      <DrawerContent
        styles={styles}
        onClose={onClose}
        onMuteAll={onMuteAll}
        onModeratePeerMedia={onModeratePeerMedia}
        onModeratePeerMediaAll={onModeratePeerMediaAll}
        headerName={profile?.displayName || "Everyone"}
        onToggleHost={onToggleHost}
        isOwner={isOwner}
        isCreator={isCreator}
        person={person}
        onMute={onMute}
        onToggleHide={toggleHidden}
        isHidden={isHidden}
        kick={kick}
        allMuted={allMuted}
        kickAll={kickAll}
        allHost={allHost}
        hasCameraShare={Boolean(person && cameraShareOwnerIds.has(person.id))}
        hasScreenShare={Boolean(person && screenShareOwnerIds.has(person.id))}
        cameraShareTargets={cameraShareTargets}
        screenShareTargets={screenShareTargets}
        onMakeAllHosts={makeAllHosts}
        people={people}
        canUpdateRoles={canUpdateRoles}
        canModerateMedia={canModerateMedia}
        canManageChatHistoryExport={canManageChatHistoryExport}
        canManageParticipantLayout={canManageParticipantLayout}
        participantLayoutRoomEnabled={participantLayoutRoomEnabled}
        participantLayoutForcedState={participantLayoutForcedState}
        onParticipantLayoutRoomStateChange={onParticipantLayoutRoomStateChange}
      />
    </ResponsiveOverlayDrawer>
  );
}

function DrawerContent({
  styles,
  onClose,
  onMuteAll,
  onModeratePeerMedia,
  onModeratePeerMediaAll,
  headerName,
  onToggleHost,
  isOwner,
  isCreator,
  person,
  people,
  onMute,
  onToggleHide,
  isHidden,
  kick,
  allMuted,
  kickAll,
  allHost,
  hasCameraShare,
  hasScreenShare,
  cameraShareTargets,
  screenShareTargets,
  onMakeAllHosts,
  canUpdateRoles,
  canModerateMedia,
  canManageChatHistoryExport,
  canManageParticipantLayout,
  participantLayoutRoomEnabled,
  participantLayoutForcedState,
  onParticipantLayoutRoomStateChange
}) {
  const [maxVolume, setMaxVolume] = useState(MAX / 2);
  const { multipliers, updatePersonMultiplier } = useAllAvatarVolume(people);
  const voiceChatEnabled = configManager.config.voiceChat.enabled;
  const [, updateLocalAudio] = useLocalAudio();
  const defaultParticipantLevel = Math.floor(MAX / 2);
  const {
    downloadChatHistory,
    downloadTranscriptionHistory,
    canDownloadChatHistory,
    canDownloadTranscriptionHistory,
    isChatHistoryDownloadConfigured,
    isDownloadingChatHistory,
    isTranscriptionHistoryDownloadConfigured,
    isDownloadingTranscriptionHistory
  } = useRoomPersistence();
  const [chatHistoryDownloadError, setChatHistoryDownloadError] = useState(null);
  const [transcriptionHistoryDownloadError, setTranscriptionHistoryDownloadError] = useState(null);

  const onLevelChanged = useCallback(
    level => {
      const parsedLevel = Number(level);
      if (!Number.isFinite(parsedLevel)) return;

      const newMultiplier = calcGainMultiplier(parsedLevel);
      if (person) {
        updatePersonMultiplier(person.id, newMultiplier);
      } else {
        people.forEach(p => {
          if (p.isMe) {
            updateLocalAudio(parsedLevel * 10);
          } else {
            updatePersonMultiplier(p.id, newMultiplier);
          }
        });
        setMaxVolume(parsedLevel);
      }
    },
    [people, person, updateLocalAudio, updatePersonMultiplier]
  );

  const participantLevel = person ? calcLevel(multipliers[person.id]) : maxVolume;
  const newLevel = Number.isFinite(participantLevel) ? participantLevel : defaultParticipantLevel;
  const bulkHostAction = getParticipantSettingsBulkHostAction({ people, allHost, canUpdateRoles });
  const hostDisabled = person ? person?.isMe || isCreator || !person?.roles?.signed_in || !canUpdateRoles : false;

  const isMod = useRole("owner");

  const kickTargets = getParticipantSettingsKickTargets(people);
  const kickDisabled = person ? person?.isMe || isCreator : !isMod || kickTargets.length === 0;
  const showAudio = person ? person?.presence !== "lobby" : true;
  const showVideoModeration =
    canModerateMedia &&
    !!onModeratePeerMedia &&
    !!onModeratePeerMediaAll &&
    (person ? !person.isMe && person?.presence !== "lobby" : true);
  const microphoneAction = getParticipantSettingsMicrophoneAction({ person, allMuted, voiceChatEnabled });
  const cameraAction = getParticipantSettingsMediaShareAction({
    person,
    source: MediaDevices.CAMERA,
    active: hasCameraShare,
    targetCount: cameraShareTargets.length
  });
  const screenShareAction = getParticipantSettingsMediaShareAction({
    person,
    source: MediaDevices.SCREEN,
    active: hasScreenShare,
    targetCount: screenShareTargets.length
  });
  const hideAction = getParticipantSettingsHideAction({ person, isHidden });
  const kickAction = getParticipantSettingsKickAction({ person });
  const normalizedParticipantLayoutForcedState = normalizeParticipantLayoutForcedState(participantLayoutForcedState);
  const participantLayoutModeForcedState = participantLayoutRoomEnabled
    ? normalizedParticipantLayoutForcedState
    : PARTICIPANT_LAYOUT_FORCED_STATE.CLOSED;
  const chatHistoryExportState = getChatHistoryExportState({
    hasPerson: Boolean(person),
    isConfigured: isChatHistoryDownloadConfigured,
    canManage: canDownloadChatHistory,
    isDownloading: isDownloadingChatHistory
  });
  const transcriptionHistoryExportState = getTranscriptionHistoryExportState({
    hasPerson: Boolean(person),
    isConfigured: isTranscriptionHistoryDownloadConfigured,
    canManage: canDownloadTranscriptionHistory,
    isDownloading: isDownloadingTranscriptionHistory
  });
  const chatHistoryExportDisabled = chatHistoryExportState?.disabled;
  const transcriptionHistoryExportDisabled = transcriptionHistoryExportState?.disabled;

  const onDownloadChatHistory = useCallback(async () => {
    if (!downloadChatHistory || chatHistoryExportDisabled) {
      return;
    }

    setChatHistoryDownloadError(null);
    try {
      await downloadChatHistory();
    } catch (error) {
      setChatHistoryDownloadError(error?.message || "Failed to download chat history.");
    }
  }, [chatHistoryExportDisabled, downloadChatHistory]);

  const onDownloadTranscriptionHistory = useCallback(async () => {
    if (!downloadTranscriptionHistory || transcriptionHistoryExportDisabled) {
      return;
    }

    setTranscriptionHistoryDownloadError(null);
    try {
      await downloadTranscriptionHistory();
    } catch (error) {
      setTranscriptionHistoryDownloadError(error?.message || "Failed to download transcription history.");
    }
  }, [downloadTranscriptionHistory, transcriptionHistoryExportDisabled]);

  const onParticipantLayoutForcedStateChange = useCallback(
    forcedState => {
      if (!onParticipantLayoutRoomStateChange) return;

      onParticipantLayoutRoomStateChange(createParticipantLayoutRoomStateForForcedStateChange(forcedState));
    },
    [onParticipantLayoutRoomStateChange]
  );

  return (
    <>
      <DrawerHeader className={styles.headerDrawer}>
        <DrawerHeaderTitle
          className={styles.drawerHeaderTitle}
          action={
            <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} onClick={() => onClose()} />
          }
        >
          {headerName}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className={styles.drawerBody}>
        <Accordion collapsible multiple defaultOpenItems={["1", "2", "3", "4", "5"]}>
          <AccordionItem value="1">
            <AccordionHeader className={styles.accordionHeader}>
              <span className={styles.accordionHeaderText}>Roles & Permissions</span>
            </AccordionHeader>
            <AccordionPanel className={styles.accordionPanel}>
              <Field label={"Host"} orientation="horizontal">
                {person ? (
                  <Switch disabled={hostDisabled} onClick={() => onToggleHost()} checked={isOwner} />
                ) : (
                  <ConfirmActionModal
                    body={bulkHostAction.body}
                    confirmLabel={bulkHostAction.label}
                    styles={styles}
                    title={bulkHostAction.title}
                    onConfirm={onMakeAllHosts}
                  >
                    <Button
                      appearance="secondary"
                      className={styles.fieldActionButton}
                      disabled={bulkHostAction.disabled}
                    >
                      {bulkHostAction.label}
                    </Button>
                  </ConfirmActionModal>
                )}
              </Field>
            </AccordionPanel>
          </AccordionItem>
          {(showAudio || showVideoModeration || canManageParticipantLayout) && <Divider />}
          {showAudio && (
            <AccordionItem value="2">
              <AccordionHeader className={styles.accordionHeader}>
                <span className={styles.accordionHeaderText}>Audio</span>
              </AccordionHeader>
              <AccordionPanel className={styles.accordionPanel}>
                <Field label={"Microphone"} orientation="horizontal">
                  <Button
                    appearance="secondary"
                    className={styles.fieldActionButton}
                    disabled={microphoneAction.disabled}
                    onClick={() => (person ? onMute(person) : onMuteAll())}
                  >
                    {microphoneAction.label}
                  </Button>
                </Field>
                {!person?.isMe && (
                  <Field label={"Volume"} orientation="horizontal">
                    <div className={styles.volumeContainer}>
                      <FluentNumericInput
                        value={person ? newLevel : maxVolume}
                        min={MIN}
                        max={MAX}
                        onChange={value => onLevelChanged(value)}
                        className={styles.compoundNumericInput}
                      />
                      <Slider
                        min={MIN}
                        max={MAX}
                        value={person ? newLevel : maxVolume}
                        onChange={(ev, data) => onLevelChanged(data.value)}
                      />
                    </div>
                  </Field>
                )}
              </AccordionPanel>
            </AccordionItem>
          )}
          {showVideoModeration && (
            <>
              <Divider />
              <AccordionItem value="3">
                <AccordionHeader className={styles.accordionHeader}>
                  <span className={styles.accordionHeaderText}>Video</span>
                </AccordionHeader>
                <AccordionPanel className={styles.accordionPanel}>
                  <Field label={"Camera"} orientation="horizontal">
                    <Button
                      appearance="secondary"
                      className={styles.fieldActionButton}
                      disabled={cameraAction.disabled}
                      onClick={() =>
                        person
                          ? onModeratePeerMedia(person, MediaDevices.CAMERA)
                          : onModeratePeerMediaAll(MediaDevices.CAMERA, cameraShareTargets)
                      }
                    >
                      {cameraAction.label}
                    </Button>
                  </Field>
                  <Field label={"Screen share"} orientation="horizontal">
                    <Button
                      appearance="secondary"
                      className={styles.fieldActionButton}
                      disabled={screenShareAction.disabled}
                      onClick={() =>
                        person
                          ? onModeratePeerMedia(person, MediaDevices.SCREEN)
                          : onModeratePeerMediaAll(MediaDevices.SCREEN, screenShareTargets)
                      }
                    >
                      {screenShareAction.label}
                    </Button>
                  </Field>
                </AccordionPanel>
              </AccordionItem>
            </>
          )}
          {canManageParticipantLayout && (
            <>
              <Divider />
              <AccordionItem value="4">
                <AccordionHeader className={styles.accordionHeader}>
                  <span className={styles.accordionHeaderText}>Layout</span>
                </AccordionHeader>
                <AccordionPanel className={styles.accordionPanel}>
                  <div className={styles.layoutManagementSection}>
                    <ParticipantLayoutBehaviorSelector
                      forcedState={participantLayoutModeForcedState}
                      onForcedStateChange={onParticipantLayoutForcedStateChange}
                      styles={styles}
                    />
                  </div>
                </AccordionPanel>
              </AccordionItem>
            </>
          )}
          {(chatHistoryExportState || transcriptionHistoryExportState) && (
            <>
              <Divider />
              <AccordionItem value="5">
                <AccordionHeader className={styles.accordionHeader}>
                  <span className={styles.accordionHeaderText}>History</span>
                </AccordionHeader>
                <AccordionPanel className={styles.accordionPanel}>
                  <Text className={styles.historySectionDescription}>{HISTORY_DOWNLOAD_HELPER_TEXT}</Text>
                  {chatHistoryExportState && (
                    <>
                      <HistoryExportActionRow
                        active={chatHistoryExportState.active}
                        buttonLabel={chatHistoryExportState.buttonLabel}
                        disabled={chatHistoryExportState.disabled}
                        label="Full chat history"
                        styles={styles}
                        onClick={onDownloadChatHistory}
                      />
                      {chatHistoryDownloadError && (
                        <div aria-live="polite" className={styles.chatHistoryError}>
                          {chatHistoryDownloadError}
                        </div>
                      )}
                    </>
                  )}
                  {transcriptionHistoryExportState && (
                    <>
                      <HistoryExportActionRow
                        active={transcriptionHistoryExportState.active}
                        buttonLabel={transcriptionHistoryExportState.buttonLabel}
                        disabled={transcriptionHistoryExportState.disabled}
                        label="Full transcription history"
                        styles={styles}
                        onClick={onDownloadTranscriptionHistory}
                      />
                      {transcriptionHistoryDownloadError && (
                        <div aria-live="polite" className={styles.chatHistoryError}>
                          {transcriptionHistoryDownloadError}
                        </div>
                      )}
                    </>
                  )}
                </AccordionPanel>
              </AccordionItem>
            </>
          )}
        </Accordion>
      </DrawerBody>
      <div className={mergeClasses(styles.footerParticipants, hideAction && styles.footerParticipantsSplit)}>
        {/* <Button appearance="suble">Invite</Button> */}
        {hideAction && (
          <Button disabled={hideAction.disabled} appearance="secondary" onClick={onToggleHide}>
            {hideAction.label}
          </Button>
        )}
        <KickModal action={kickAction} person={person} styles={styles} kick={kick} kickAll={kickAll}>
          <Button disabled={kickDisabled} appearance="secondary">
            {kickAction.buttonLabel}
          </Button>
        </KickModal>
      </div>
    </>
  );
}

function HistoryExportActionRow({ active, buttonLabel, disabled, label, onClick, styles }) {
  return (
    <div className={styles.historyActionRow}>
      <Text as="span" className={styles.historyActionLabel}>
        {label}
      </Text>
      <Button
        appearance="secondary"
        className={styles.fieldActionButton}
        disabled={disabled}
        icon={active ? <Spinner size="tiny" /> : <ArrowDownload24Regular />}
        onClick={onClick}
      >
        {buttonLabel}
      </Button>
    </div>
  );
}

HistoryExportActionRow.propTypes = {
  active: PropTypes.bool.isRequired,
  buttonLabel: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  styles: PropTypes.shape({
    fieldActionButton: PropTypes.string.isRequired,
    historyActionLabel: PropTypes.string.isRequired,
    historyActionRow: PropTypes.string.isRequired
  }).isRequired
};

function ParticipantLayoutBehaviorSelector({ forcedState, onForcedStateChange, styles }) {
  return (
    <div className={styles.layoutBehaviorRow}>
      <RadioGroup
        aria-label={PARTICIPANT_LAYOUT_MODE_LABEL}
        className={styles.layoutBehaviorOptions}
        value={forcedState}
        onChange={(_, data) => onForcedStateChange(data.value)}
      >
        {PARTICIPANT_LAYOUT_BEHAVIOR_OPTIONS.map(option => (
          <Radio
            key={option.value}
            className={styles.layoutBehaviorRadio}
            data-participant-layout-behavior-option={option.value}
            label={
              <span className={styles.layoutBehaviorRadioContent}>
                <Text as="span" className={styles.layoutBehaviorRadioText}>
                  {option.label}
                </Text>
                <Text as="span" className={styles.layoutBehaviorDescription}>
                  {option.description}
                </Text>
              </span>
            }
            value={option.value}
          />
        ))}
      </RadioGroup>
    </div>
  );
}

function ConfirmActionModal({ body, children, confirmLabel, onConfirm, styles, title }) {
  return (
    <Dialog>
      <DialogTrigger>{children}</DialogTrigger>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger>
                <Button appearance="transparent" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            {title}
          </DialogTitle>
          <DialogContent>{body}</DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
            <DialogTrigger disableButtonEnhancement>
              <Button onClick={onConfirm} appearance="primary">
                {confirmLabel}
              </Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function KickModal({ action, children, styles, kick, kickAll, person }) {
  return (
    <Dialog>
      <DialogTrigger>{children}</DialogTrigger>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger>
                <Button appearance="transparent" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            {action.title}
          </DialogTitle>
          <DialogContent className={styles.dialogContent}>
            <div>{action.body}</div>
            <div>
              <Checkbox label={"Complete an abuse report"} />
            </div>
            <div>
              <Checkbox label={"Lock in this Verse"} />
            </div>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
            <Button onClick={person ? kick : kickAll} appearance="primary">
              {action.confirmLabel}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
